/**
 * Per-portal workbench test configs + deadline-tracker placeholders.
 *
 * Per §4.3: each portal exposes 1-2 deliberately-bad presets that
 * trigger Mutaba'aa vetoes inline through the existing
 * mutabaa_precheck functions. No backend changes — the existing
 * portal endpoints already run apply_precheck before any portal
 * client call, so a bad preset returns 422 with the veto reason in
 * the response body.
 */

import type { Portal } from "./HealthCard";
import type { VetoTestConfig } from "./MutabaaVetoTestForm";

export interface DeadlineConfig {
  id: string;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
  article: string;
}

export interface PortalConfig {
  portal: Portal;
  titleKey: string;
  titleFallback: string;
  subtitleKey: string;
  subtitleFallback: string;
  arTitleKey: string;
  arTitleFallback: string;
  workbenchTests: VetoTestConfig[];
  deadlineTrackers: DeadlineConfig[];
}

// Returns yyyy-mm-dd N days ago, computed at call time so the
// "5 days ago" preset is always past the Mudad 3-day window.
function isoNDaysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Mudad — Article 88 (3-day) + IBAN-match
// ---------------------------------------------------------------------------

const _SAMPLE_KSA_IBAN = "SA0380000000608010167519";

export const MUDAD_CONFIG: PortalConfig = {
  portal: "mudad",
  titleKey: "gov.portal_mudad_title",
  titleFallback: "Mudad / WPS",
  subtitleKey: "gov.portal_mudad_doc",
  subtitleFallback:
    "Wage Protection System — submit SIF files for each payroll period.",
  arTitleKey: "gov.portal_mudad_ar",
  arTitleFallback: "نظام حماية الأجور · مدد",
  workbenchTests: [
    {
      id: "mudad-article-88-3d",
      titleKey: "gov.mudad.test_article_88",
      descKey: "gov.mudad.test_article_88_desc",
      endpoint: "/api/gov/mudad/submit-sif",
      // 5 days ago = past the Article 88 / WPS 3-day window. The
      // precheck_mudad_sif veto fires before any portal client call.
      get presetPayload() {
        return {
          payroll_period_end: isoNDaysAgo(5),
          employee_count: 5,
          total_amount_sar: "60035.94",
          sif_blob_ref: "sandbox-blob-stub",
        };
      },
      expectedVetoArticle: "WPS 3-day window",
    },
    {
      id: "mudad-iban-match",
      titleKey: "gov.mudad.test_iban_match",
      descKey: "gov.mudad.test_iban_match_desc",
      endpoint: "/api/gov/mudad/validate-iban",
      // KSA IBAN that doesn't match the employee's confirmed bank.
      presetPayload: {
        iban: _SAMPLE_KSA_IBAN,
        employee_confirmed_iban: "SA9999999999999999999999",
      },
      expectedVetoArticle: "Mudad-confirmed bank rule",
    },
  ],
  deadlineTrackers: [
    {
      id: "mudad-sif-deadline",
      titleKey: "gov.mudad.deadline_sif",
      titleFallback: "SIF submission deadline",
      descKey: "gov.mudad.deadline_sif_desc",
      descFallback:
        "WPS requires the SIF file within 3 days of payroll period end. Per-PayrollRun countdown surfaces once accredited.",
      article: "WPS 3-day window",
    },
    {
      id: "mudad-payment-confirmation",
      titleKey: "gov.mudad.deadline_payment",
      titleFallback: "Payment confirmation",
      descKey: "gov.mudad.deadline_payment_desc",
      descFallback:
        "Per-employee payment confirmation polled from Mudad once submission is processed.",
      article: "Mudad confirmation",
    },
  ],
};

// ---------------------------------------------------------------------------
// GOSI — Article 88 (15-day) + M/33 amount
// ---------------------------------------------------------------------------

export const GOSI_CONFIG: PortalConfig = {
  portal: "gosi",
  titleKey: "gov.portal_gosi_title",
  titleFallback: "GOSI",
  subtitleKey: "gov.portal_gosi_doc",
  subtitleFallback:
    "Social Insurance — register employees + file monthly M/33 contributions.",
  arTitleKey: "gov.portal_gosi_ar",
  arTitleFallback: "التأمينات الاجتماعية",
  workbenchTests: [
    {
      id: "gosi-m33-amount",
      titleKey: "gov.gosi.test_m33_amount",
      descKey: "gov.gosi.test_m33_amount_desc",
      endpoint: "/api/gov/gosi/file-monthly-contribution",
      // Deliberately-wrong total — should be 1800 for SAR 10K Saudi base
      // (9% employer + 9% employee). 999 misses by ~45%.
      presetPayload: {
        period: "2026-05",
        employee_count: 1,
        total_amount_sar: "999.00",
        expected_total_amount_sar: "1800.00",
        breakdown: [
          {
            national_id: "1234567890",
            is_saudi: true,
            monthly_base_sar: "10000.00",
            employer_amount_sar: "499.50",
            employee_amount_sar: "499.50",
          },
        ],
      },
      expectedVetoArticle: "Article M/33",
    },
    {
      id: "gosi-article-88-15d",
      titleKey: "gov.gosi.test_article_88",
      descKey: "gov.gosi.test_article_88_desc",
      endpoint: "/api/gov/gosi/terminate",
      // 30 days ago = past the Article 88 / 15-day window
      get presetPayload() {
        return {
          gosi_employee_number: "GOSI-SBX-123456",
          termination_effective_date: isoNDaysAgo(30),
          reason_code: "resignation",
        };
      },
      expectedVetoArticle: "Article 88",
    },
  ],
  deadlineTrackers: [
    {
      id: "gosi-article-88-15d",
      titleKey: "gov.gosi.deadline_article_88",
      titleFallback: "Article 88 — 15-day termination window",
      descKey: "gov.gosi.deadline_article_88_desc",
      descFallback:
        "Every termination must reach GOSI within 15 days. Per-termination countdown surfaces once accredited.",
      article: "Article 88",
    },
    {
      id: "gosi-monthly-contribution",
      titleKey: "gov.gosi.deadline_monthly",
      titleFallback: "Monthly contribution deadline",
      descKey: "gov.gosi.deadline_monthly_desc",
      descFallback:
        "Monthly M/33 contribution due by the 15th of the following month.",
      article: "Article M/33",
    },
  ],
};

// ---------------------------------------------------------------------------
// Qiwa — Saudization warn-not-block
// ---------------------------------------------------------------------------

export const QIWA_CONFIG: PortalConfig = {
  portal: "qiwa",
  titleKey: "gov.portal_qiwa_title",
  titleFallback: "Qiwa",
  subtitleKey: "gov.portal_qiwa_doc",
  subtitleFallback:
    "MHRSD — contract registration + Saudization (Nitaqat) compliance.",
  arTitleKey: "gov.portal_qiwa_ar",
  arTitleFallback: "قوى",
  workbenchTests: [
    {
      id: "qiwa-saudization-warn",
      titleKey: "gov.qiwa.test_saudization",
      descKey: "gov.qiwa.test_saudization_desc",
      endpoint: "/api/gov/qiwa/register-contract",
      // Non-Saudi hire on an LE with 25% Saudization commitment +
      // currently sitting at 25%. The warn-not-block precheck fires —
      // the registration still succeeds but `saudization_warning` is
      // returned in the response body. UI renders the warn card.
      presetPayload: {
        national_id: "1112223334",
        full_name_en: "Rajesh Kumar",
        contract_start_date: new Date().toISOString().slice(0, 10),
        contract_end_date: null,
        is_saudi: false,
        current_saudization_pct: 25.0,
        le_commitment_pct: 25.0,
        current_headcount: 100,
      },
      expectedVetoArticle: "Nitaqat (warn-not-block)",
    },
  ],
  deadlineTrackers: [
    {
      id: "qiwa-saudization",
      titleKey: "gov.qiwa.deadline_saudization",
      titleFallback: "Saudization (Nitaqat) band",
      descKey: "gov.qiwa.deadline_saudization_desc",
      descFallback:
        "Daily Nitaqat-band scanner polls Qiwa and flags drops below LE commitment.",
      article: "Nitaqat commitment",
    },
    {
      id: "qiwa-iqama-validity",
      titleKey: "gov.qiwa.deadline_iqama",
      titleFallback: "Iqama validity (90 days)",
      descKey: "gov.qiwa.deadline_iqama_desc",
      descFallback:
        "Qiwa contract registration requires iqama valid for ≥90 days.",
      article: "Qiwa iqama floor",
    },
  ],
};

// ---------------------------------------------------------------------------
// Muqeem — iqama quota
// ---------------------------------------------------------------------------

export const MUQEEM_CONFIG: PortalConfig = {
  portal: "muqeem",
  titleKey: "gov.portal_muqeem_title",
  titleFallback: "Muqeem",
  subtitleKey: "gov.portal_muqeem_doc",
  subtitleFallback:
    "MOI / Absher Business — iqama lifecycle + dependent registration.",
  arTitleKey: "gov.portal_muqeem_ar",
  arTitleFallback: "مقيم",
  workbenchTests: [
    {
      id: "muqeem-iqama-quota",
      titleKey: "gov.muqeem.test_iqama_quota",
      descKey: "gov.muqeem.test_iqama_quota_desc",
      endpoint: "/api/gov/muqeem/register-dependent",
      // Employee already at the iqama_allowed_quota — adding one more
      // dependent triggers the precheck_muqeem_dependent veto.
      presetPayload: {
        iqama_number: "9876543210",
        dependent_national_id: "5556667778",
        relationship: "spouse",
        current_dependent_count: 4,
        iqama_allowed_quota: 4,
      },
      expectedVetoArticle: "MOI dependent quota",
    },
  ],
  deadlineTrackers: [
    {
      id: "muqeem-iqama-60-30-15",
      titleKey: "gov.muqeem.deadline_iqama_renewal",
      titleFallback: "Iqama renewal windows (60 / 30 / 15 days)",
      descKey: "gov.muqeem.deadline_iqama_renewal_desc",
      descFallback:
        "Per-employee iqama expiry tracker. Auto-initiate renewal at 30 days when HR-approved.",
      article: "MOI iqama lifecycle",
    },
    {
      id: "muqeem-travel-permit",
      titleKey: "gov.muqeem.deadline_travel_permit",
      titleFallback: "Travel permit validity",
      descKey: "gov.muqeem.deadline_travel_permit_desc",
      descFallback:
        "Exit-reentry permit lifecycle per active traveler.",
      article: "Muqeem travel",
    },
  ],
};

export const PORTAL_CONFIGS: Record<Portal, PortalConfig> = {
  mudad: MUDAD_CONFIG,
  gosi: GOSI_CONFIG,
  qiwa: QIWA_CONFIG,
  muqeem: MUQEEM_CONFIG,
};
